import React, { useEffect, useRef, useState } from 'react';
import Button from '@sats-group/fresh-fitness-ui-lib/react/button';
import Link from '@sats-group/fresh-fitness-ui-lib/react/link';
import LinkButton from '@sats-group/fresh-fitness-ui-lib/react/link-button';
import Message from '@sats-group/fresh-fitness-ui-lib/react/message';
import Text from '@sats-group/fresh-fitness-ui-lib/react/text';

import Checkbox from 'root/client/components/checkbox/checkbox';
import ContentContainer from 'root/client/components/content-container/content-container';
import HiddenInput from 'root/client/components/hidden-input/hidden-input';
import Modal from 'root/client/components/modal/modal';
import PhoneInput from 'root/client/components/phone-input/phone-input';
import RadioButton from 'root/client/components/radio-button/radio-button';
import RichText from 'root/client/components/rich-text/rich-text';
import TextField from 'root/client/components/text-field/text-field';
import useToggle from 'root/client/hooks/use-toggle';
import apiHelper from 'root/client/ts/api-helper';
import { fillRegistrationForm } from 'root/client/ts/registration-form-filler';
import SalesLayout from 'root/client/components/sales-layout/sales-layout';

import type { CheckoutAboutYou as Props } from './checkout-about-you.types';

const CheckoutAboutYou: React.FC<Props> = ({
  debug,
  form,
  layout,
  previous,
}) => {
  const [isOpen, , openTerms, closeTerms] = useToggle();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [postalArea, setPostalArea] = useState<string>();
  const [postalCodeErrorMessage, setPostalCodeErrorMessage] =
    useState<string>();

  const postalCodeValue = useRef('');

  useEffect(() => {
    const resetSubmitting = () => setIsSubmitting(false);
    window.addEventListener('pageshow', resetSubmitting);
    return () => window.removeEventListener('pageshow', resetSubmitting);
  }, []);

  const handleSubmit: React.SubmitEventHandler<HTMLFormElement> = event => {
    if (isSubmitting) {
      event.preventDefault();
      return;
    }

    setIsSubmitting(true);
  };

  const getPostalArea = (value: string) => {
    if (value.length < 4) {
      return;
    }

    setPostalArea('');
    setPostalCodeErrorMessage('');
    postalCodeValue.current = value;

    apiHelper
      .get(`${form.inputs.postalCode.endpoint}/${value}`)
      .then(data => {
        if (postalCodeValue.current === value) {
          setPostalArea(data.name);
        }
      })
      .catch(e => {
        if (e.responseStatus === 400 || e.responseStatus === 404) {
          setPostalCodeErrorMessage(
            form.inputs.postalCode.errorMessages.notFound,
          );
        } else {
          setPostalCodeErrorMessage(
            form.inputs.postalCode.errorMessages.unknownError,
          );
        }
      });
  };

  const handlePostalCodeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    getPostalArea(event.target.value);
  };

  const handleTermsClick: Parameters<typeof Link>[0]['onClick'] = event => {
    event.preventDefault();
    openTerms();
  };

  useEffect(() => {
    if (form.inputs.postalCode.input.defaultValue) {
      getPostalArea(form.inputs.postalCode.input.defaultValue);
    }
  }, []);

  return (
    <SalesLayout {...layout} isLoading={isSubmitting}>
      <ContentContainer width={ContentContainer.widths.Medium}>
        {debug ? (
          <div className="checkout-about-you__debug">
            <Button text="Fill form" onClick={fillRegistrationForm} />
          </div>
        ) : null}
        <div className="checkout-about-you">
          <form
            className="checkout-about-you__form"
            action={form.endpoint}
            method="POST"
            onSubmit={handleSubmit}
          >
            <Text
              theme={Text.themes.emphasis}
              size={Text.sizes.header1}
              elementName="h2"
            >
              {form.title}
            </Text>
            <div className="checkout-about-you__form-inputs">
              {form.inputs.hiddenInputs.map(input => (
                <HiddenInput {...input} />
              ))}
              {postalArea ? (
                <HiddenInput {...form.inputs.postalArea} value={postalArea} />
              ) : null}
              <TextField {...form.inputs.firstName} wide />
              <TextField {...form.inputs.lastName} wide />
              <TextField {...form.inputs.email} wide />
              <PhoneInput {...form.inputs.phone} required />
              <TextField {...form.inputs.birthday} />
              <TextField {...form.inputs.address} wide />
              <div className="checkout-about-you__form-input checkout-about-you__form-input--split">
                <TextField
                  {...form.inputs.postalCode.input}
                  inputMode="numeric"
                  onChange={handlePostalCodeChange}
                />
                {postalArea ? (
                  <div>
                    <Text>{postalArea}</Text>
                  </div>
                ) : null}
              </div>
              {postalCodeErrorMessage ? (
                <div>
                  <Message text={postalCodeErrorMessage} type="error" />
                </div>
              ) : null}
            </div>
            <div className="checkout-about-you__form-options">
              {form.inputs.genders.title}
              {form.inputs.genders.options.map(radio => (
                <RadioButton key={radio.value} {...radio} />
              ))}
            </div>
            {form.inputs.optIns ? (
              <div className="checkout-about-you__form-options">
                {form.inputs.optIns.options.map(checkbox => (
                  <Checkbox key={checkbox.name} {...checkbox} />
                ))}
                <div>
                  <Link
                    {...form.inputs.optIns.membershipTerms.open}
                    aria-expanded={isOpen}
                    data-test-terms-trigger
                    onClick={handleTermsClick}
                  />
                </div>
                <Modal
                  {...form.inputs.optIns.membershipTerms.modal}
                  hide={closeTerms}
                  isVisible={isOpen}
                >
                  <div className="checkout-about-you__terms">
                    <RichText {...form.inputs.optIns.membershipTerms.intro} />
                    <RichText {...form.inputs.optIns.membershipTerms.body} />
                  </div>
                </Modal>
              </div>
            ) : null}
            <div className="checkout-about-you__actions">
              <div>
                <LinkButton
                  {...previous}
                  theme={LinkButton.themes.secondary}
                  wide
                />
              </div>
              <div>
                <Button type="submit" text={form.submitLabel} wide />
              </div>
            </div>
          </form>
        </div>
      </ContentContainer>
    </SalesLayout>
  );
};

export default CheckoutAboutYou;
